import { useSelector } from "react-redux";

import type { Feature, Geometry, Polygon } from "geojson";

import {
  removeStartFromUnderscore,
  stringArrayToNumberArray
} from "@/helpers/string";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import type { RootState } from "@/store";
import type { Dataset } from "@/types/dataset";
import type {
  AnalysisOutput,
  AreaInput,
  Catchment,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import {
  AreaInputType,
  type CatchmentDataset,
  ECatchmentInputBy,
  ECatchmentTransport,
  ECatchmentType,
  EOutputAnalysisType,
  EOutputGridType,
  ERoadAccessMeasurement,
  ScoringOptionsEnum
} from "@/types/spatial";
import { iterateRows } from "@/utils/catchment";
import {
  createFinalAttributes,
  getGridConfig,
  getMaxLevelAdminArea,
  mapDatasetFilters,
  type MappedFilter,
  processCatchmentDatasetAttributes,
  separateAdminAreaAttributes
} from "@/utils/configurationMapper";
import { convertDatasetResponseToFeatureCollection } from "@/utils/dataset";
import { isNumericAttribute } from "@/utils/datasetAttributes";
import {
  getIsIncludedScoring,
  getNegativeImpactValue,
  parseWeightValue
} from "@/utils/weightValues";

import { pointOnFeature } from "@turf/turf";

const useConfigurationMappers = () => {
  const { isAdminTreeEnabled, isBVTDataForCatchmentEnabled } = useAppFeatures();
  const {
    datasets: currentDatasets,
    filters: datasetFilters,
    visualizationOrder: currentVisualizationOrder,
    weightValues,
    history
  } = useSelector((state: RootState) => state.dataset);

  const {
    adminAreaSettings,
    adminTreeSettings,
    catchmentSettings,
    spatialConfig,
    polygonSettings,
    overrideGlobalFilter
  } = useSelector((state: RootState) => state.spatial);
  const { showAnalysisResultPopup } = useSelector(
    (state: RootState) => state.ui
  );
  const {
    adminAreaSettings: globalAdminAreaSettings,
    selectedAreaIds: globalSelectedAreaIds
  } = useSelector((state: RootState) => state.globalFilter);

  /* Check if the analysis result popup is open, will get the datasets from history
    Reference: file useAnalysisResult.tsx line 49
  */
  const datasets = showAnalysisResultPopup ? history.datasets : currentDatasets;
  const visualizationOrder = showAnalysisResultPopup
    ? history.visualizationOrder
    : currentVisualizationOrder;

  // Build a DatasetConfigResponse from a single catchment dataset entry —
  // shared by both the legacy `dataset` (singular) and the new `datasets`
  // (array) payload field.
  const buildCatchmentDatasetConfig = (
    entry: CatchmentDataset
  ): DatasetConfigResponse => ({
    id: entry.rawDataset?.dataset_id,
    name: entry.rawDataset?.name,
    display_name: entry.dataset?.data?.display_name,
    spatial_aggregation_type: entry.rawDataset?.spatial_aggregation_type,
    attributes: processCatchmentDatasetAttributes(entry.dataset, entry.filters),
    color_based: entry.dataset?.selectedCategory ?? undefined,
    label_config: {
      ...entry.dataset?.style?.labelConfig,
      enable_ranking: entry.dataset?.style?.rankConfig?.isEnabled ?? false,
      max_ranking: entry.dataset?.style?.rankConfig?.maxRanking ?? 10
    },
    weight: parseWeightValue(weightValues[entry.rawDataset?.dataset_id]?.weight)
  });

  const mapCatchmentAreaInput = (): AreaInput | null => {
    if (!catchmentSettings) return null;
    const {
      catchmentType,
      roadAccessConfig,
      radiusData = [],
      placeCoordinates
    } = catchmentSettings;
    const byRadius = catchmentType === ECatchmentType.BY_RADIUS;
    const isDistance =
      !byRadius &&
      roadAccessConfig?.measurementType === ERoadAccessMeasurement.DISTANCE;

    const inputByLocation =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_LOCATION;
    const inputByDataset =
      catchmentSettings.catchmentInputBy === ECatchmentInputBy.BY_DATASET;
    const hasCoordinates = placeCoordinates && placeCoordinates.length > 0;
    const catchmentByDataset = Object.values(
      catchmentSettings.datasetConfig?.datasets ?? {}
    );
    const isochroneData = roadAccessConfig?.isochroneData;

    let locations: Catchment["locations"] = undefined;

    const isPolygonCatchmentDataset =
      isBVTDataForCatchmentEnabled &&
      inputByDataset &&
      catchmentByDataset.some(
        (entry) => entry.dataset?.data?.spatial_aggregation_type === "polygon"
      );

    if (inputByLocation && hasCoordinates) {
      locations = placeCoordinates.map((place, index) => ({
        name: place.locationName || `Marker #${index + 1}`,
        coordinates: {
          latitude: place.lat,
          longitude: place.lng
        },
        catchment_area: isochroneData?.[index]?.data ?? null // send isochrone data instead of null, because on API analysis/:id/config does not return isochrone data
      }));
    } else if (isPolygonCatchmentDataset) {
      /** Polygon catchment dataset uses the same { dataset(s), locations }
       * shape as POI catchment dataset. Each polygon row → one Location
       * whose `coordinates` is the polygon centroid and `catchment_area`
       * carries the polygon itself as a FeatureCollection. MultiPolygon
       * rows are split into one Location per ring set. When the multi-
       * dataset flag is on, every location carries a `dataset_id` linking
       * it back to its source dataset.
       *
       * Two-stage pipeline:
       * 1. flatMap polygon datasets → features → rings into a flat list of
       *    `{ coordinates, datasetId }` so a global 1-based sequence can be
       *    derived from the array index.
       * 2. map each ring into a Location with centroid + polygon payload.
       */
      const extractRings = (geometry: Geometry | undefined): number[][][][] => {
        if (geometry?.type === "Polygon") return [geometry.coordinates];
        if (geometry?.type === "MultiPolygon") return geometry.coordinates;
        return [];
      };
      const ringEntries = catchmentByDataset
        .filter(
          (entry) => entry.dataset?.data?.spatial_aggregation_type === "polygon"
        )
        .flatMap((entry) => {
          const features = convertDatasetResponseToFeatureCollection(
            entry.rawDataset
          ).features;
          return features.flatMap((feature) =>
            extractRings(feature.geometry).map((coordinates) => ({
              coordinates,
              datasetId: entry.rawDataset?.dataset_id
            }))
          );
        });
      locations = ringEntries.map(({ coordinates, datasetId }, index) => {
        const polygonSeq = index + 1;
        const polygonFeature: Feature<Polygon> = {
          type: "Feature",
          geometry: { type: "Polygon", coordinates },
          properties: {
            name: `Location layer polygon #${polygonSeq}`,
            sequence: polygonSeq
          }
        };
        const [lng, lat] = pointOnFeature(polygonFeature).geometry.coordinates;
        return {
          name: `Location layer polygon #${polygonSeq}`,
          coordinates: { latitude: lat, longitude: lng },
          catchment_area: [
            {
              type: "FeatureCollection",
              features: [polygonFeature]
            }
          ],
          ...(isBVTDataForCatchmentEnabled && datasetId
            ? { dataset_id: datasetId }
            : {})
        };
      });
    } else if (inputByDataset && !byRadius && catchmentByDataset.length > 0) {
      /** POI dataset rows → Location[]. Iterate per dataset so we can stamp
       * each location with its `dataset_id` when the multi-dataset flag is
       * enabled, while preserving the global isochrone index used by the
       * existing single-dataset payload.
       */
      const locationResults: Catchment["locations"] = [];
      let globalIndex = 0;
      for (const entry of catchmentByDataset) {
        const datasetId = entry.rawDataset?.dataset_id;
        iterateRows([entry], (long, lat, label) => {
          locationResults.push({
            name: label ?? "",
            coordinates: { latitude: lat, longitude: long },
            catchment_area: isochroneData?.[globalIndex]?.data ?? null,
            ...(isBVTDataForCatchmentEnabled && datasetId
              ? { dataset_id: datasetId }
              : {})
          });
          globalIndex += 1;
        });
      }
      locations = locationResults;
    }

    const hasCatchmentDatasets =
      inputByDataset && catchmentByDataset.length > 0;
    const useMultiDataset =
      isBVTDataForCatchmentEnabled && hasCatchmentDatasets;

    /** Polygon catchment dataset hides the catchment-type section in the UI,
     * so radius/road config never gets set. BE still validates contours
     * (must be 1+ items in range 1..100000 for distance) — send a valid
     * dummy so the request passes; polygon catchment derives its area from
     * `locations[].catchment_area`, not from the contours value.
     */
    const contours = byRadius
      ? stringArrayToNumberArray(radiusData)
      : isDistance
        ? stringArrayToNumberArray(roadAccessConfig.distances)
        : stringArrayToNumberArray(roadAccessConfig?.durations);
    const sanitizedContours =
      isPolygonCatchmentDataset && contours.length === 0 ? [1] : contours;

    return {
      adm_area_id: undefined,
      catchment: {
        profile:
          (byRadius
            ? ECatchmentType.BY_RADIUS
            : roadAccessConfig?.routingType) ?? ECatchmentTransport.WALKING,
        exclusions: byRadius
          ? ([] as string[])
          : (roadAccessConfig?.roadExclusions as string[]),
        metric: byRadius
          ? "distance"
          : (roadAccessConfig?.measurementType ?? ""),
        contours: sanitizedContours,
        locations,
        // Multi-dataset: send `datasets` array (BE rejects when set
        // together with `dataset`). Legacy: send `dataset` singular for
        // back-compat with old BE clients.
        ...(useMultiDataset
          ? { datasets: catchmentByDataset.map(buildCatchmentDatasetConfig) }
          : hasCatchmentDatasets
            ? { dataset: buildCatchmentDatasetConfig(catchmentByDataset[0]) }
            : {})
      }
    };
  };

  const mapAdminAreaInput = (): AreaInput | null => {
    // When admin tree is enabled, use the first explicitly selected area ID
    if (isAdminTreeEnabled && adminTreeSettings?.selectedAreaIds?.length > 0) {
      return {
        adm_area_ids: adminTreeSettings.selectedAreaIds,
        is_global_admin_area: !overrideGlobalFilter
      };
    }

    // Fallback to dropdown-based selection
    const maxLevel = getMaxLevelAdminArea(adminAreaSettings);
    if (!maxLevel) return null;
    return {
      adm_area_id: adminAreaSettings?.[maxLevel]?.value,
      is_global_admin_area: !overrideGlobalFilter
    };
  };

  const mapCustomCatchmentInput = (): AreaInput | null => {
    if (Object.keys(polygonSettings).length === 0) return null;

    return {
      custom_catchments: {
        type: "FeatureCollection",
        features: Object.values(polygonSettings).map((polygon) => ({
          ...polygon.polygon
        }))
      }
    };
  };

  const mappingAreaInput = (): AreaInput => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.CATCHMENT:
        /** BY_POLYGON catchment is sent as custom_catchments so BE accepts it
         * (a catchment with empty contours fails validation). Polygon
         * catchment DATASET (BY_DATASET) uses the same { catchment.datasets,
         * catchment.locations } shape as POI catchment dataset — handled by
         * mapCatchmentAreaInput which branches on dataset.spatial_aggregation_type.
         */
        if (
          isBVTDataForCatchmentEnabled &&
          catchmentSettings?.catchmentInputBy === ECatchmentInputBy.BY_POLYGON
        ) {
          return mapCustomCatchmentInput() ?? {};
        }
        return mapCatchmentAreaInput() ?? {};
      case AreaInputType.POLYGON:
        return mapCustomCatchmentInput() ?? {};
      default:
        return mapAdminAreaInput() ?? {};
    }
  };

  const mappingAnalysisOutput = (): AnalysisOutput | undefined => {
    if (!spatialConfig.outputType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    if (spatialConfig.outputType === EOutputAnalysisType.PROFILING)
      return {
        type: EOutputAnalysisType.PROFILING,
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };

    if (!spatialConfig.gridType) {
      return {
        scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
      };
    }

    const geohashType = spatialConfig.gridType === EOutputGridType.GEOHASH;
    const gridLevel = Number(
      geohashType
        ? (spatialConfig.gridResolution?.value ?? 0)
        : (spatialConfig.h3Resolution?.value ?? 0)
    );
    return {
      type: spatialConfig.gridType ?? "",
      grid_level: gridLevel,
      scoring_option: spatialConfig.scoringOption ?? ScoringOptionsEnum.RAW
    };
  };

  // Filter datasets by acceptable spatial types
  const filterDatasetsByType = () => {
    return Object.entries(datasets).filter(([, dataset]) =>
      [
        "poi",
        "polygon",
        "adm_area",
        "line",
        EOutputAnalysisType.H3,
        EOutputAnalysisType.GEOHASH,
        "line"
      ].includes(dataset.properties.spatialType)
    );
  };

  const processDatasetAttributes = (
    datasetId: string
  ): DatasetConfigAttributeResponse[] => {
    const filtersArray = datasetFilters[datasetId] ?? [];
    const weightScore = weightValues[datasetId] ?? {};
    const filters: MappedFilter[] = filtersArray.map((filter) =>
      mapDatasetFilters(filter)
    );
    const currDataset = datasets[datasetId];
    const attributeSource =
      currDataset.properties.spatialType === "H3" &&
      currDataset.data.source === "bvt"
        ? currDataset.data.schema
        : currDataset.categories;

    // Track matched filters to prevent the same filter from being matched to multiple attributes
    // This can happen when one attribute's id equals another's paired_key_attribute_id
    // Note: Admin area filters are excluded from this deduplication because they intentionally
    // match multiple schema items (adm_area_level_1, adm_area_level_2, etc.) and get deduplicated later
    const matchedFilterKeys = new Set<string>();

    return (
      attributeSource?.map((attribute) => {
        const attributeId = attribute.paired_key_attribute_id ?? attribute.id;
        let matchedIsAdmArea = false;

        const matchedFilter = filters.find((f) => {
          const isAdmAreaFilter = f.filter?.criteria?.includes("::");

          if (isAdmAreaFilter) {
            // Admin area filter has ::. For admin area filters, only check attribute id
            if (f.attributeId === attributeId) {
              matchedIsAdmArea = true;
              return true;
            }
          } else {
            // For non-admin-area filters, skip if already matched to prevent duplication
            const filterKey = `${f.attributeId}_${f.attributeCode ?? ""}`;
            if (matchedFilterKeys.has(filterKey)) return false;

            if (
              f.attributeId === attributeId &&
              f.attributeCode === attribute.attribute_code
            ) {
              matchedIsAdmArea = false;
              return true;
            }
          }
          return false;
        }) || { filter: null, filterType: null };

        // Mark non-admin-area filters as matched to prevent duplication
        if (matchedFilter.filter && !matchedIsAdmArea) {
          const filterKey = `${(matchedFilter as MappedFilter).attributeId}_${(matchedFilter as MappedFilter).attributeCode}`;
          matchedFilterKeys.add(filterKey);
        }

        const { filter, filterType } = matchedFilter;

        const attributeEntry = weightScore?.attributes?.[attributeId];

        const negativeImpact = getNegativeImpactValue(attributeEntry);

        return {
          filter,
          id: attributeId,
          weight_score: parseWeightValue(attributeEntry),
          ...(negativeImpact !== undefined
            ? {
                weight_negative_impact: negativeImpact
              }
            : {}),
          include_scoring:
            isNumericAttribute(attribute) &&
            getIsIncludedScoring(attributeEntry),
          ...(filterType ? { filterType } : {}),
          ...(attribute.attribute_code
            ? { attribute_code: attribute.attribute_code }
            : {})
        };
      }) || []
    );
  };

  const mappingDataset = (): DatasetConfigResponse[] => {
    if (!Object.keys(datasets).length) return [];

    const filteredDatasets = filterDatasetsByType();

    // Optimize lookup by creating a Map for O(1) access
    const datasetMap = new Map(filteredDatasets);

    const orderedFilteredDatasets = visualizationOrder
      .filter((datasetId) => datasetMap.has(datasetId))
      .map(
        (datasetId) =>
          [datasetId, datasetMap.get(datasetId)] as [
            string,
            Dataset | undefined
          ]
      )
      .filter((entry): entry is [string, Dataset] => entry[1] !== undefined);

    return orderedFilteredDatasets.map(([datasetId, dataset]) => {
      const attributes = processDatasetAttributes(datasetId);

      // nonAdminAttributes can have non-unique attribute id
      // Thus, the Set cannot be used to filter them
      const { adminAreaAttributes, nonAdminAttributes } =
        separateAdminAreaAttributes(attributes, dataset);

      const finalAttributes = createFinalAttributes(
        nonAdminAttributes,
        adminAreaAttributes
      );

      return {
        id: removeStartFromUnderscore(datasetId),
        display_name: dataset?.data?.display_name,
        spatial_aggregation_type: dataset.properties.spatialType,
        attributes: finalAttributes,

        color_based: dataset?.selectedCategory,
        color_scale: dataset?.style?.colorFamily,
        opacity:
          dataset.properties.spatialType === "line"
            ? dataset?.style?.stroke?.opacity
            : dataset?.style?.fillOpacity,
        grid_config: getGridConfig(dataset),
        color_steps: dataset?.style?.colorSteps,
        stroke_width:
          dataset.properties.spatialType === "line"
            ? dataset?.style?.stroke?.width
            : undefined,
        label_config: {
          ...dataset?.style?.labelConfig,
          enable_ranking: dataset?.style?.rankConfig?.isEnabled ?? false,
          max_ranking: dataset?.style?.rankConfig?.maxRanking ?? 10
        },
        weight: parseWeightValue(weightValues[datasetId]?.weight),
        include_scoring: weightValues[datasetId]?.include_scoring !== false,
        is_global_admin_area: dataset.is_global_admin_area ?? false,
        ...(dataset.group_by === "none" ? {} : { group_by: dataset.group_by })
      };
    });
  };

  const mappingAdmAreaIds = (): string[] => {
    if (isAdminTreeEnabled) {
      return globalSelectedAreaIds;
    }
    const maxLevel = getMaxLevelAdminArea(globalAdminAreaSettings);
    if (!maxLevel) return [];

    const value = globalAdminAreaSettings?.[maxLevel]?.value;
    return value ? [value] : [];
  };

  return {
    getGridConfig,
    mappingDataset,
    mappingAreaInput,
    mappingAdmAreaIds,
    mappingAnalysisOutput
  };
};

export default useConfigurationMappers;
